























































































































































import { LogLevel, LogMessage, ValidationResponse } from '@openticket/lib-shop';
import { AxiosError, isAxiosError } from 'axios';
import Vue, { VueConstructor } from 'vue';
import Component from 'vue-class-component';
import { Provide, Watch } from 'vue-property-decorator';
import { NavigationGuardNext, Route } from 'vue-router';
import { Action, State } from 'vuex-class';
import { ListenerFactory } from '@/pages/shop/listeners/factory';
import { scrollTo, scrollToOffset } from '@/utils';
import isOpenTicketLogMessage from '@/utils/is_log_message';
import SplashScreen from '../../../components/SplashScreen.vue';
import ShopHeader from '../components/ShopHeader.vue';
import OrderBar from '../components/OrderBar.vue';
import ShopModuleComponent, {
    ShopModuleShopContentModifiers,
} from '../modules/module';
import TimeslotsFilter from '../modules/timeslots/Timeslots.vue';
import { TimeslotFilter, toTimeRangeString } from '../modules/timeslots';
import '../modules';
import { ShopDates, ShopPage } from '../store/types';
import { isDeferredOrder } from '@/composables/deferred';

const ORDER_SLOW_THRESHOLD = 5000;

@Component({
    components: {
        ShopHeader,
        OrderBar,
        TimeslotsFilter,
        SplashScreen,
    },
})
export default class PagesView extends Vue {
    @State('pages') pages!: ShopPage[];
    @State('dates') dates!: ShopDates;
    @Action('nextPage') actionNextPage!: () => void;

    title = '';
    valid = false;
    loading = false;
    placingOrder = false;
    placeOrderSuccess = false;

    @Provide('timeslotFilter')
    timeslotFilter: { currentFilter: TimeslotFilter | null } = {
        currentFilter: null,
    };

    showTimeslotWarning = false;

    paymentUrl = '';
    orderRedirectIsSlow = false;

    dynamicListenerFactory?: ListenerFactory;

    shopContentModifiers: {
        state: ShopModuleShopContentModifiers | null;
    } = Vue.observable({ state: null });

    /**
     * Handles the change of content modifiers set by the ExternalValidationForm
     * Applies coupons to the cart, whitelisting and blacklisting is not done here but by `filterShopContent`
     * @param shopContentModifiers
     */
    async onChangeShopContentModifiersEvent(
        shopContentModifiers: ShopModuleShopContentModifiers
    ): Promise<void> {
        await this.$shop.cart.removeAllItems();

        this.shopContentModifiers.state = shopContentModifiers;
        if (shopContentModifiers && shopContentModifiers.coupon) {
            try {
                await this.$shop.cart.addCoupon(shopContentModifiers.coupon);
            } catch (e) {
                console.error(e);
            }
        }
    }

    created(): void {
        if (this.$settings?.dynamic?.shop?.listeners) {
            this.dynamicListenerFactory = new ListenerFactory(
                this.$settings?.dynamic?.shop.listeners,
                this.$shop,
                this.shopContentModifiers,
                (slug: string, data: object) => {
                    const message = this.$t(slug, data);

                    this.$shop.notifications.send({
                        message:
                            typeof message === 'string'
                                ? message
                                : 'Unexpected error',
                        type: 'is-danger',
                    });
                }
            );
        }
        this.onPageChange();
    }

    beforeRouteEnter(to: Route, _from: Route, next: NavigationGuardNext): void {
        // Change legacy eventDate query parameter to the new timeslot param.
        const { eventDate: eventDateId } = to.query;

        if (eventDateId && typeof eventDateId === 'string') {
            const query = {
                ...to.query,
            };
            delete query.eventDate;

            const eventDate = this.$shop.data.event_dates_map[eventDateId];

            if (eventDate) {
                query.timeslot = toTimeRangeString(eventDate);
            }

            next({
                ...to,
                name: to.name || undefined,
                query,
            });

            return;
        }

        next();
    }

    get showOrderBarTotal(): boolean {
        // Only hide the total from order bar if cart review is the last module on the page
        return (
            this.page.modules[this.page.modules.length - 1].name !==
            'cart-review'
        );
    }

    get page(): ShopPage {
        const name = this.$route.params.page;
        return this.pages.find(
            (page: ShopPage) => page.name === name
        ) as ShopPage;
    }

    get index(): number {
        const name = this.$route.params.page;
        return this.pages.findIndex((page: ShopPage) => page.name === name);
    }

    get prevPage(): ShopPage | undefined {
        const name = this.$route.params.page;
        const index = this.pages.findIndex(
            (page: ShopPage) => page.name === name
        );
        return this.pages[index - 1];
    }

    get nextPage(): ShopPage | undefined {
        const name = this.$route.params.page;
        const index = this.pages.findIndex(
            (page: ShopPage) => page.name === name
        );
        return this.pages[index + 1];
    }

    get showTimeslots(): boolean {
        const modules_names = this.page.modules.map(m => m.name);
        return (
            !!this.$shop.data.greedy_date_selection &&
            (modules_names.includes('tickets') ||
                modules_names.includes('ticket') ||
                modules_names.includes('swinkels-individual-tickets'))
        );
    }

    refreshShop(): void {
        // eslint-disable-next-line no-self-assign
        window.location.href = window.location.href;
    }

    getModules(): ShopModuleComponent[] {
        const components: any[] = [];

        for (let i = 0; i < (this.page.modules || []).length; i++) {
            let elem = this.$refs[`shop_module_${i}`];

            if (Array.isArray(elem)) {
                elem = elem[0];
            }

            if (elem) {
                components.push(elem);
            }
        }

        return components;
    }

    next(): void {
        if (this.showTimeslots && !this.validateTimeslots()) {
            return;
        }

        const valid = this.validatePage(true);

        if (valid) {
            this.actionNextPage();
        }
    }

    validateTimeslots(): boolean {
        if (
            !this.$settings ||
            !this.$settings.static ||
            !this.$settings.static.shop.enableTimeslotsFilters
        ) {
            return true;
        }

        if (!this.$route.query.timeslot) {
            // Simulate shop-module error
            this.showTimeslotWarning = true;
            this.$notifications.danger(
                this.$t('shop.components.shop.select_timeslot') as string
            );
            scrollTo({
                element: this.$refs.timeslots as HTMLDivElement,
            });

            setTimeout(() => {
                this.showTimeslotWarning = false;
            }, 750);

            return false;
        }

        return true;
    }

    async placeOrder(): Promise<void> {
        const valid = this.validatePage(true);

        if (!valid) {
            return;
        }

        this.placingOrder = true;

        let orderSuccessful = false;

        try {
            await this.$shop.cart.finalizePaymentMethod();

            const paymentMethodValidation: ValidationResponse = this.$shop.cart.validator.paymentMethod(
                true,
                true
            );

            if (!paymentMethodValidation.valid) {
                const modules: ShopModuleComponent[] = this.getModulesByScopes([
                    'payment',
                ]);

                if (modules[0]) {
                    modules[0].isDirty = false;
                }

                this.showError(paymentMethodValidation.message, modules);

                return;
            }

            if (this.$cookies.categories.organiserMarketing.confirmed) {
                this.$shop.cart.setOrderTracking(true);
            }

            const response = await this.$shop.cart.placeOrder();

            orderSuccessful = true;

            if (isDeferredOrder(response)) {
                this.$shop.log.create(
                    LogLevel.Info,
                    ['place-order', 'deferred'],
                    {
                        pollintervalms: response.pollintervalms,
                        polllimit: response.polllimit,
                        sub: response.sub,
                    },
                    'Order is deferred'
                );

                const query: {
                    pollintervalms?: string;
                    polllimit?: string;
                } = {};

                if ('pollintervalms' in response && response.pollintervalms) {
                    query.pollintervalms = response.pollintervalms.toFixed(0);
                }

                if (
                    'polllimit' in response &&
                    typeof response.polllimit === 'number'
                ) {
                    query.polllimit = response.polllimit.toFixed(0);
                }

                await this.$router.push({
                    name: 'deferred-place-order',
                    params: {
                        shop_id: this.$route.params.shop_id,
                        deferred_token: response.sub,
                    },
                });
                return;
            }

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            this.paymentUrl = response.redirectUrl;

            // Show actions when redirect is taking a long time
            setTimeout(() => {
                this.orderRedirectIsSlow = true;
            }, ORDER_SLOW_THRESHOLD);

            // If the shop is loaded inside an iframe, overwrite the location of its parent
            if (window.top !== window.self) {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                window.top.location = response.redirectUrl;

                // Else, overwrite its own location
            } else {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                window.location = response.redirectUrl;
            }
        } catch (e) {
            // We do not want anything but processed errors!
            if (!isOpenTicketLogMessage(e)) {
                this.$shop.notifications.send({
                    message: `${e}`,
                    type: 'is-danger',
                });

                throw e;
            }

            this.processPlaceOrderLogMessage(e);
        } finally {
            if (!orderSuccessful) {
                // Enable order button again
                this.placingOrder = false;
            }
        }
    }

    onDirtyEvent(): void {
        this.valid = this.validatePage(false);
    }

    validatePage(mutate?: boolean): boolean {
        const modules = this.getModules();

        for (let i = 0; i < modules.length; i++) {
            const module = modules[i];

            const config = this.page.modules[i];

            // Check for validation errors
            let error = module.validate(mutate);

            module.isDirty = false;

            if (error) {
                if (mutate) {
                    const message = error.slug
                        ? (this.$t(error.slug, error.slugData) as string)
                        : error.message;

                    this.showError(message, [module]);
                }
                return false;
            }

            const comp = this.getVueShopModuleComponent(config.name);

            // For a component to be valid, it should also be ready
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            error = comp.isReady ? comp.isReady(this.$shop) : null;

            if (error) {
                if (mutate) {
                    this.showError('Something went wrong', [module]);
                }
                return false;
            }
        }

        return this.nextPageReady(mutate);
    }

    nextPageReady(mutate?: boolean): boolean {
        if (this.nextPage) {
            for (const comp of this.nextPage.modules) {
                const elem = this.getVueShopModuleComponent(comp.name);

                if (!elem) {
                    throw Error(`Module "${comp.name}" not found`);
                }

                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                const error = elem.isReady ? elem.isReady(this.$shop) : null;

                if (error) {
                    if (mutate) {
                        const modules = this.getModulesByScopes(error.scopes);

                        const message = error.slug
                            ? (this.$t(error.slug, error.slugData) as string)
                            : error.message;

                        this.showError(message, modules);
                    }
                    return false;
                }
            }
        }
        return true;
    }

    showError(error: string, modules: ShopModuleComponent[]): void {
        this.$shop.notifications.send({
            message: error,
            type: 'is-danger',
        });

        const cls = 'shop-module-focus';
        const regex = new RegExp('(\\s|^)' + cls + '(\\s|$)');

        for (let i = 0; i < modules.length; i++) {
            const module = modules[i];
            const element = module.$el;

            if (module && element) {
                // Scroll to the first element
                if (i === 0) {
                    const offset =
                        module.$el.getBoundingClientRect().top + window.scrollY;

                    scrollToOffset(Math.max(offset - 80, 0));
                }

                element.className += ' ' + cls;

                setTimeout(() => {
                    element.className = element.className.replace(regex, ' ');
                }, 750);
            }
        }
    }

    // noinspection JSMethodCanBeStatic
    getVueShopModuleComponent(name: string): VueConstructor {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        return Vue.options.components[`shop-module-${name}`];
    }

    getModulesByScopes(scopes: string[]): ShopModuleComponent[] {
        let modules = this.getModules();

        modules = modules.filter(
            (module: ShopModuleComponent) =>
                module.scopes.filter((scope: string) => scopes.includes(scope))
                    .length > 0
        );

        return modules;
    }

    processPlaceOrderLogMessage(e: LogMessage): void {
        let scope!: string;
        let message!: string;

        if (
            // Specifically handle stripe errors
            e.slug ===
            'sdkshop.log.cart.finalizePaymentMethod.error.stripe.error'
        ) {
            scope = 'payment';
            message = this.$t(e.slug) as string;

            if (e.data.message) {
                message += ` ${e.data.message}`;
            }
        } else if (e.exception && isAxiosError(e.exception)) {
            const exception: AxiosError<{
                errors?: Array<{ subjectType?: string; reason?: string }>;
            }> = e.exception;

            if (
                // Check if the response data has the expected format
                exception &&
                exception.response &&
                exception.response.data &&
                exception.response.data.errors &&
                exception.response.data.errors.length &&
                exception.response.data.errors[0]
            ) {
                // Get the scope and message based on the subjectType
                switch (exception.response.data.errors[0].subjectType) {
                    case 'Payment':
                        scope = 'payment';
                        message = this.$t(
                            exception.response.data.errors[0].reason || ''
                        ) as string;

                        break;
                    case 'Ticket':
                        scope = 'tickets';
                        message = this.$t(
                            exception.response.data.errors[0].reason || ''
                        ) as string;

                        break;
                    default:
                        this.$shop.notifications.send({
                            message: JSON.stringify(
                                exception.response.data.errors[0]
                            ),
                            type: 'is-danger',
                        });

                        throw e;
                }
            }
        }

        // If at least one of them is unknown, the context is unknown
        if (!scope || !message) {
            this.$shop.notifications.send({
                message: this.marshalPlaceOrderErrorDescription(e),
                type: 'is-danger',
            });

            throw e;
        }

        // Find the components that are to blame for the error
        const components = this.getModulesByScopes([scope]);

        if (!components || components.length < 1) {
            this.$shop.notifications.send({
                message,
                type: 'is-danger',
            });

            throw e;
        }

        // If found, show the error to the user
        this.showError(message, components);
    }

    private marshalPlaceOrderErrorDescription(e: any): string {
        if (!e._isOpenTicketLogMessage) {
            return `${e}`;
        }

        if (!e.exception?.response?.data?.error_description) {
            return this.$t(
                e.slug ||
                    (e.data &&
                        e.data.exception &&
                        (e.data.exception.slug || e.data.exception.message)) ||
                    e.exception ||
                    `${e}`
            ) as string;
        }

        const errorDescription = e.exception.response.data.error_description;

        if (typeof errorDescription === 'string') {
            return this.$t(errorDescription) as string;
        }

        if (Array.isArray(errorDescription) && errorDescription.length > 0) {
            return errorDescription
                .map((item): string => {
                    return this.$t(`${item}`) as string;
                })
                .join(', ');
        }

        if (
            Object.getOwnPropertyNames(errorDescription) &&
            Object.getOwnPropertyNames(errorDescription).length
        ) {
            return Object.getOwnPropertyNames(errorDescription)
                .map((key: string) => {
                    const val = errorDescription[key];

                    if (!Array.isArray(val) || !val.length) {
                        return this.$t(`${val}`) as string;
                    }

                    return val
                        .map((item): string => {
                            return this.$t(`${item}`) as string;
                        })
                        .join(', ');
                })
                .join(', ');
        }

        return `${e}`;
    }

    onTimeslotFilterUpdated(filter: TimeslotFilter): void {
        this.timeslotFilter.currentFilter = filter;
    }

    beforeDestroy(): void {
        if (this.dynamicListenerFactory) {
            this.dynamicListenerFactory.destroy();
        }
    }

    @Watch('page')
    onPageChange(): void {
        this.valid = false;
        this.$nextTick(() => {
            this.title = this.page.title;
            this.onDirtyEvent();
        });
    }
}
